import {
  AccountInfo,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from "@azure/msal-browser";

import {
  ameTenantId,
  corpTenantId,
  pmeTenantId,
  serviceClientId,
} from "./Environment";

const OoCTApplicationId = "d55abcb5-59cd-4881-9453-1288bdb56783";
const aadConfig = {
  [OoCTApplicationId]: {
    authority:
      "https://login.microsoftonline.com/72f988bf-86f1-41af-91ab-2d7cd011db47",
    defaultScope: `api://${OoCTApplicationId}/.default`,
  },
}[serviceClientId]!;

export type TenantName = "AME" | "PME" | "Corp";

export const tenantIdNameMap: Record<string, TenantName> = {
  [ameTenantId]: "AME",
  [pmeTenantId]: "PME",
  [corpTenantId]: "Corp",
};

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: serviceClientId,
    authority: aadConfig.authority,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "localStorage",
  },
});

/**
 * Utility function to extract loginHint from query parameters
 */
export function getLoginHintFromQuery(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("loginHint");
}

/**
 * Check if the app is running inside an iFrame
 */
export function isInIFrame(): boolean {
  return window.self !== window.top;
}

export async function initializeMsal() {
  await msalInstance.initialize();
  msalInstance.enableAccountStorageEvents();
}

/**
 * Silent authentication using ssoSilent for iFrame compatibility
 * Falls back to loginPopup if silent auth fails
 */
export async function ssoSilentAuth(options?: {
  scopes?: string | string[];
  loginHint?: string | null;
}): Promise<any> {
  const loginHint = options?.loginHint || getLoginHintFromQuery();
  const scopes = options?.scopes || ["openid", "profile", "User.Read"];
  const scopesArray = typeof scopes === "string" ? [scopes] : scopes;

  if (!loginHint) {
    throw new Error("LoginHint is required for ssoSilent authentication");
  }

  const silentRequest = {
    scopes: scopesArray,
    loginHint: loginHint,
  };

  try {
    // Call ssoSilent() to get auth token with no pop ups or redirects
    const response = await msalInstance.ssoSilent(silentRequest);
    // Ensure the account is set as active if it isn't already
    if (response.account && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(response.account);
    }
    return response;

    // If ssoSilent() fails, use loginPopup() as a fallback. This will open a pop up for authentication
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      const popupRequest = {
        scopes: ["openid", "profile", "User.Read"],
        loginHint: loginHint,
      };
      const loginResponse = await msalInstance.loginPopup(popupRequest);
      // Ensure the account is set as active
      if (loginResponse.account && !msalInstance.getActiveAccount()) {
        msalInstance.setActiveAccount(loginResponse.account);
      }
    } else {
      throw error;
    }
  }
}

export async function loginRedirect(isRetry?: boolean): Promise<void> {
  // Use ssoSilent to authenticate in iFrame
  if (isInIFrame()) {
    await ssoSilentAuth();
  } else {
    // Redirect users to login for normal browser window
    try {
      await msalInstance.loginRedirect({
        scopes: ["openid", "profile"],
        prompt: "select_account",
      });
    } catch (error) {
      if (
        !isRetry &&
        (error as BrowserAuthError).errorCode ===
          BrowserAuthErrorCodes.interactionInProgress
      ) {
        return loginRedirect(true);
      }

      throw error;
    }
  }
}

export async function logoutRedirect(
  account: AccountInfo,
  isRetry?: boolean,
): Promise<void> {
  // Check if we're in an iFrame - use logoutPopup instead of redirect
  if (isInIFrame()) {
    try {
      await msalInstance.logoutPopup({
        account,
        postLogoutRedirectUri: window.location.href,
      });
      // Clear the active account and clear cache tokens
      msalInstance.setActiveAccount(null);
      await msalInstance.clearCache();
      window.location.reload();
    } catch (error) {
      // Clear the active account and clear cache tokens
      msalInstance.setActiveAccount(null);
      await msalInstance.clearCache();
      window.location.reload();
      throw error;
    }
  } else {
    // Normal browser window - use redirect logout
    try {
      await msalInstance.logoutRedirect({
        account,
        postLogoutRedirectUri: window.location.href,
      });
    } catch (error) {
      const itemKey = "msal.interaction.status";
      if (
        !isRetry &&
        (error as BrowserAuthError).errorCode ===
          BrowserAuthErrorCodes.interactionInProgress &&
        sessionStorage.getItem(itemKey)
      ) {
        sessionStorage.removeItem(itemKey);
        return logoutRedirect(account, true);
      }

      throw error;
    }
  }
}

export async function handleRedirectPromise() {
  try {
    await msalInstance.handleRedirectPromise();
  } catch (error) {
    if (
      (error as BrowserAuthError).errorCode !==
      BrowserAuthErrorCodes.noTokenRequestCacheError
    ) {
      throw error;
    }
  }
}

export async function acquireToken(options?: {
  scopes?: string | string[];
  corpOnly?: boolean;
}) {
  const scopes = options?.scopes || aadConfig.defaultScope;
  const scopesArray = typeof scopes === "string" ? [scopes] : scopes;
  const authority = options?.corpOnly
    ? "https://login.microsoftonline.com/72f988bf-86f1-41af-91ab-2d7cd011db47"
    : undefined;
  let account: AccountInfo | null = null;
  if (options?.corpOnly) {
    const activeAccount = msalInstance.getActiveAccount();
    account =
      activeAccount?.tenantId === corpTenantId
        ? activeAccount
        : msalInstance
            .getAllAccounts()
            .find((x) => x.tenantId === corpTenantId) || null;
    if (!account) {
      throw new Error("Please add a Corp account to complete this request.");
    }
  } else {
    account = msalInstance.getActiveAccount();
    if (!account) {
      throw new Error(
        "Please select a primary account to complete this request.",
      );
    }
  }

  try {
    return await msalInstance.acquireTokenSilent({
      scopes: scopesArray,
      account,
      authority,
    });
  } catch (e) {
    // Use ssoSilent to authenticate in iFrame
    if (isInIFrame()) {
      await ssoSilentAuth();
      const newAccount = msalInstance.getActiveAccount();
      if (newAccount) {
        return await msalInstance.acquireTokenSilent({
          scopes: scopesArray,
          account: newAccount,
          authority,
        });
      }
      throw new Error("Failed to authenticate or retrieve account in iFrame");
    } else {
      await msalInstance.acquireTokenRedirect({
        scopes: scopesArray,
        account,
        authority,
      });
      throw e;
    }
  }
}
